import React from "react";
import { Box, Button, CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../../themes/redesignTokens";

export interface FormActionBarProps {
  onCancel: () => void;
  onSubmit?: () => void;
  submitLabel: string;
  cancelLabel?: string;
  submitIcon?: React.ReactNode;
  submitDisabled?: boolean;
  loading?: boolean;
  variant?: "standard" | "pill";
  animation?: boolean;
}

/**
 * Bottom action row with cancel button + gradient submit button.
 *
 * Combined from WO (standard layout) + SR (pill variant and icon).
 * Cancel: text button with dt.textSecondary.
 * Submit: gradient button using accentGradientDark / accentGradientDarkHover.
 */
const FormActionBar: React.FC<FormActionBarProps> = ({
  onCancel,
  onSubmit,
  submitLabel,
  cancelLabel = "Cancel",
  submitIcon,
  submitDisabled,
  loading,
  variant = "standard",
  animation = true,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  const borderRadiusValue =
    variant === "pill" ? dt.radiusPill : dt.radiusSm;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 1.5,
        mt: 3,
        ...(animation
          ? { animation: "fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) 0.1s both" }
          : {}),
      }}
    >
      <Button
        variant="text"
        onClick={onCancel}
        sx={{
          fontFamily: dt.font,
          fontWeight: 600,
          fontSize: isPhone ? "13px" : "14px",
          textTransform: "none",
          color: dt.textSecondary,
          borderRadius: dt.radiusSm,
          px: 2.5,
          py: 1,
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: dt.inputBg,
            color: dt.textPrimary,
          },
        }}
      >
        {cancelLabel}
      </Button>

      <Button
        type={onSubmit ? "button" : "submit"}
        onClick={onSubmit}
        disabled={submitDisabled || loading}
        variant="contained"
        startIcon={
          loading ? (
            <CircularProgress size={16} sx={{ color: "#fff" }} />
          ) : (
            submitIcon
          )
        }
        sx={{
          fontFamily: dt.font,
          fontWeight: 600,
          fontSize: "13px",
          textTransform: "none",
          borderRadius: borderRadiusValue,
          padding: "8px 24px",
          background: accentGradientDark,
          color: "#fff",
          boxShadow: "0 2px 8px rgba(52,152,219,0.18)",
          "&:hover": {
            background: accentGradientDarkHover,
            boxShadow: "0 4px 14px rgba(52,152,219,0.35)",
          },
          "&.Mui-disabled": {
            background: "#E5E7EB",
            color: "#9CA3AF",
          },
        }}
      >
        {submitLabel}
      </Button>
    </Box>
  );
};

export default FormActionBar;
